import React, {useMemo} from "react";
import Styles from "./styles.module.scss";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";

import anketaIcon from '../../../theme/assets/svg/tc_cab_capsule_anketa_icon.svg';
import materialsIcon from '../../../theme/assets/svg/tc_cab_capsule_materials_icon.svg';
import notesIcon from '../../../theme/assets/svg/tc_cab_capsule_notes_icon.svg';
import sparkIcon from '../../../theme/assets/svg/tc_cab_capsule_spark_icon.svg';
import lockIcon from '../../../theme/assets/svg/tc_cab_capsule_lock_icon.svg';

import {
    setCapsuleBuyModalStatus,
    setCapsuleQuestionnaireModalStatus,
    setSealModalStatus
} from "../../../store/cabinet/action";
import {
    CAPSULE_SECTION_WEIGHT,
    capsulePercentHandler,
    capsuleSectionPointsHandler,
    isCapsuleEditable,
    isCapsuleLocked,
    pluralHandler
} from "../../../helpers/capsule";

// с этой заполненности предлагаем закрыть капсулу
const SEAL_FROM_PERCENT = 80;

// Геометрия кольца: радиус дуг и длина окружности считаются один раз —
// из них получаются stroke-dasharray всех сегментов
const RING_SIZE = 256;
const RING_RADIUS = 104;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
// зазор между секторами, чтобы дуги не сливались в сплошное кольцо
const RING_GAP = 4;

const CabinetCapsuleMain = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const capsuleStats = useSelector(store => store.cabinet.capsuleStats);

    const stats = capsuleStats?.stats;

    const sections = useMemo(()=>{
        const questionnaire = stats?.questionnaire;
        const files = stats?.files;
        const notes = stats?.notes;

        return [
            {
                key: 'questionnaire',
                icon: anketaIcon,
                name: t('Анкета'),
                weight: CAPSULE_SECTION_WEIGHT.questionnaire,
                percent: questionnaire?.percent || 0,
                points: capsuleSectionPointsHandler(stats, 'questionnaire'),
                meta: `${questionnaire?.answered || 0} ${t('из')} ${questionnaire?.total || 0} ${pluralHandler(questionnaire?.total, [t('вопроса'), t('вопросов'), t('вопросов')])}`
            },
            {
                key: 'files',
                icon: materialsIcon,
                name: t('Памятные материалы'),
                weight: CAPSULE_SECTION_WEIGHT.files,
                percent: files?.percent || 0,
                points: capsuleSectionPointsHandler(stats, 'files'),
                meta: `${files?.count || 0} ${pluralHandler(files?.count, [t('файл'), t('файла'), t('файлов')])}`
            },
            {
                key: 'notes',
                icon: notesIcon,
                name: t('Заметки'),
                weight: CAPSULE_SECTION_WEIGHT.notes,
                percent: notes?.percent || 0,
                points: capsuleSectionPointsHandler(stats, 'notes'),
                meta: `${notes?.count || 0} ${pluralHandler(notes?.count, [t('заметка'), t('заметки'), t('заметок')])}`
            }
        ];
    }, [stats, t]);

    const totalPercent = capsulePercentHandler(stats);
    // капсула не куплена: разделы закрыты, кольцо пустое, активна только покупка
    const isLocked = isCapsuleLocked(capsuleStats?.capsule_status);

    // Сегменты идут по часовой стрелке от верха: анкета занимает правую
    // половину кольца, материалы — низ слева, заметки — верх слева
    const ringSegments = useMemo(()=>{
        let offset = 0;

        return sections.map((section)=>{
            const length = (RING_CIRCUMFERENCE * section.weight) / 100;
            const segment = {
                key: section.key,
                rotate: (offset / RING_CIRCUMFERENCE) * 360 - 90,
                trackLength: Math.max(length - RING_GAP, 0),
                fillLength: isLocked
                    ? 0
                    : Math.max(((length - RING_GAP) * Math.min(section.points / (section.weight || 1), 1)), 0)
            };
            offset += length;
            return segment;
        });
    }, [sections, isLocked]);

    const isFull = totalPercent >= 100;

    const status = capsuleStats?.capsule_status;
    const isSealed = status === 'sealed';
    // закрывать предлагаем только пока капсулу ещё можно менять
    const canSeal = isCapsuleEditable(status) && totalPercent >= SEAL_FROM_PERCENT;

    return (
        <section className={`${Styles.container}`}>
            <div className={`${Styles.contentWrap} ${Styles.cabinetPC}`}>
                <div className={`${Styles.cardWrap} ${Styles.border3dOuterXl}`}>
                    <div className={`${Styles.cardTitle}`}>{t('Моя капсула')}</div>

                    <div className={`${Styles.ringWrap}`}>
                        <svg
                            className={`${Styles.ring}`}
                            viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                        >
                            <circle className={`${Styles.ringBase}`} cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_SIZE / 2 - 2}/>
                            {ringSegments.map((segment)=>(
                                <g key={'capsule-ring_' + segment.key} transform={`rotate(${segment.rotate} ${RING_SIZE / 2} ${RING_SIZE / 2})`}>
                                    <circle
                                        className={`${Styles.ringTrack}`}
                                        cx={RING_SIZE / 2}
                                        cy={RING_SIZE / 2}
                                        r={RING_RADIUS}
                                        strokeDasharray={`${segment.trackLength} ${RING_CIRCUMFERENCE - segment.trackLength}`}
                                    />
                                    <circle
                                        className={`${Styles.ringFill}`}
                                        cx={RING_SIZE / 2}
                                        cy={RING_SIZE / 2}
                                        r={RING_RADIUS}
                                        strokeDasharray={`${segment.fillLength} ${RING_CIRCUMFERENCE - segment.fillLength}`}
                                    />
                                </g>
                            ))}
                            <circle className={`${Styles.ringCore} ${isLocked ? Styles.ringCoreLocked : ''}`} cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_RADIUS - 16}/>
                        </svg>

                        {isLocked ? (
                            <div className={`${Styles.ringLabel} ${Styles.ringLabelLocked}`}>
                                <div className={`${Styles.ringLockWrap}`}>
                                    <img src={lockIcon} alt=""/>
                                </div>
                                <div className={`${Styles.ringLockName}`}>{t('Капсула недоступна')}</div>
                            </div>
                        ) : (
                            <div className={`${Styles.ringLabel}`}>
                                <div className={`${Styles.ringLabelName}`}>{t('Капсула заполнена на')}</div>
                                <div className={`${Styles.ringLabelValue}`}>{totalPercent}%</div>
                            </div>
                        )}

                        {sections.map((section, index)=>(
                            <div
                                className={`${Styles.ringWeight} ${Styles['ringWeight_' + index]}`}
                                key={'capsule-ring-weight_' + section.key}
                            >
                                {section.weight}%
                            </div>
                        ))}
                    </div>

                    <div className={`${Styles.breakdownWrap} ${isLocked ? Styles.breakdownWrapLocked : ''}`}>
                        {sections.map((section, index)=>(
                            <React.Fragment key={'capsule-breakdown_' + section.key}>
                                {index ? <div className={`${Styles.breakdownDivider}`}/> : null}
                                <div className={`${Styles.breakdownRow}`}>
                                    <div className={`${Styles.breakdownIconWrap}`}>
                                        <img src={section.icon} alt={section.name}/>
                                    </div>
                                    <div className={`${Styles.breakdownPos}`}>
                                        <div className={`${Styles.breakdownTop}`}>
                                            <span className={`${Styles.breakdownName}`}>{section.name}</span>
                                            {isLocked ? (
                                                <span className={`${Styles.breakdownValueLocked}`}>––</span>
                                            ) : (
                                                <span className={`${Styles.breakdownValue}`}>
                                                    {section.points} / {section.weight}%
                                                </span>
                                            )}
                                        </div>
                                        {!isLocked ? (
                                            <div className={`${Styles.breakdownBottom}`}>
                                                <span className={`${Styles.breakdownMeta}`}>{section.meta}</span>
                                                {section.points >= section.weight ? (
                                                    <span className={`${Styles.breakdownStatus}`}>{t('Заполнено')}</span>
                                                ) : (
                                                    <span className={`${Styles.breakdownStatus} ${Styles.breakdownStatusLeft}`}>
                                                        {t('Осталось')} {section.weight - section.points}%
                                                    </span>
                                                )}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            </React.Fragment>
                        ))}
                    </div>

                    {isLocked ? (
                        <div
                            className={`${Styles.cardBtn} ${Styles.cardBtnAccent}`}
                            onClick={()=>{dispatch(setCapsuleBuyModalStatus(true))}}
                        >
                            <span>{t('Купить капсулу')}</span>
                        </div>
                    ) : isSealed ? (
                        // капсула запечатана — следующий шаг именно цифровая копия
                        <div
                            className={`${Styles.cardBtn} ${Styles.cardBtnAccent}`}
                            onClick={()=>{navigate('/cabinet/avatar')}}
                        >
                            <img src={sparkIcon} alt=""/>
                            <span>{t('Создать аватар')}</span>
                        </div>
                    ) : isFull ? (
                        // заполнена, но не запечатана: копию собирать ещё не из
                        // чего — гасим кнопку и объясняем это наведением
                        <div className={`${Styles.hintWrap}`}>
                            <div className={`${Styles.cardBtn} ${Styles.cardBtnDsb}`}>
                                <img src={sparkIcon} alt=""/>
                                <span>{t('Создать аватар')}</span>
                            </div>
                            <div className={`${Styles.hintBubble}`}>
                                {t('Запечатайте капсулу перед созданием аватара')}
                            </div>
                        </div>
                    ) : (
                        <div
                            className={`${Styles.cardBtn}`}
                            onClick={()=>{
                                if (capsuleStats){
                                    dispatch(setCapsuleQuestionnaireModalStatus(true));
                                }
                            }}
                        >
                            <span>{t('Продолжить заполнение')}</span>
                        </div>
                    )}

                    {canSeal ? (
                        <div className={`${Styles.sealWrap}`}>
                            <div className={`${Styles.sealNote}`}>
                                {t('Капсулу можно закрыть — содержимое зафиксируется навсегда')}
                            </div>
                            <div
                                className={`${Styles.sealBtn}`}
                                onClick={()=>{dispatch(setSealModalStatus(true))}}
                            >
                                <img src={lockIcon} alt=""/>
                                <span>{t('Закрыть капсулу')}</span>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </section>
    );
};

export default CabinetCapsuleMain;
